import React from "react";
import { Play, Pause, Flame, Plus, MoreHorizontal, Headphones } from "lucide-react";
import { usePlayer } from "@/contexts/PlayerContext";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import SmartImage from "@/components/SmartImage";
import { formatTime, mediumArt } from "@/lib/api";
import { toast } from "sonner";
import FireBadge from "@/components/FireBadge";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { api } from "@/lib/api";

const TrackRow = ({ track, index, queue, showAlbum = true }) => {
  const { current, playing, togglePlay, playTracks, toggleLike, isLiked } = usePlayer();
  const { user, login } = useAuth();
  const isCurrent = current?.trackId === track.trackId;
  const isPlayingThis = isCurrent && playing;
  const liked = isLiked(track.trackId);
  const isFull = track._source === "audius" || track._fullTrack;
  const [playlists, setPlaylists] = React.useState([]);

  const handlePlay = () => {
    if (isCurrent) {
      togglePlay();
    } else {
      if (!track.previewUrl) {
        toast.error("No preview available for this track");
        return;
      }
      playTracks(queue || [track], index || 0);
    }
  };

  const handleLike = async () => {
    if (!user) {
      toast.message("Sign in to save songs", { action: { label: "Sign in", onClick: login } });
      return;
    }
    await toggleLike(track);
  };

  const loadMyPlaylists = async () => {
    if (!user) return;
    try {
      const r = await api.get("/playlists");
      setPlaylists(r.data);
    } catch {
      /* ignore */
    }
  };

  const addTo = async (pid) => {
    try {
      await api.post(`/playlists/${pid}/tracks`, track);
      toast.success("Added to playlist");
    } catch {
      toast.error("Failed to add");
    }
  };

  return (
    <div
      data-testid={`track-row-${track.trackId}`}
      className="group grid grid-cols-[24px_1fr_auto] sm:grid-cols-[24px_1fr_1fr_auto_auto] items-center gap-4 px-4 py-2 rounded-lg hover:bg-white/5 transition cursor-default"
    >
      <button
        onClick={handlePlay}
        data-testid={`track-play-${track.trackId}`}
        className="w-6 h-6 flex items-center justify-center text-white/50 group-hover:text-white"
      >
        {isPlayingThis ? (
          <div className="flex items-end gap-[2px] h-4">
            <span className="eq-bar h-full" />
            <span className="eq-bar h-full" />
            <span className="eq-bar h-full" />
          </div>
        ) : (
          <>
            <span className="group-hover:hidden text-sm tabular-nums">{(index ?? 0) + 1}</span>
            <Play size={14} className="hidden group-hover:block" fill="currentColor" />
          </>
        )}
      </button>

      <div className="flex items-center gap-3 min-w-0">
        <SmartImage
          src={mediumArt(track.artworkUrl100)}
          alt={track.trackName || ""}
          seed={track.trackName || track.artistName || "?"}
          className="w-10 h-10 rounded-md overflow-hidden shrink-0 hidden sm:block"
          imgClassName="w-full h-full object-cover"
        />
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <p className={`text-sm font-medium truncate ${isCurrent ? "text-[#FF2E00]" : "text-white"}`}>
              {track.trackName}
            </p>
            <FireBadge item={track} size="sm" />
            {isFull && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#7C5CFF]/20 text-[#A98FFF] font-bold whitespace-nowrap">FULL</span>
            )}
          </div>
          <div className="text-xs text-white/50 truncate flex items-center gap-2">
            {track.artistId ? (
              <Link to={`/artist/${track.artistId}`} className="hover:text-white hover:underline">
                {track.artistName}
              </Link>
            ) : (
              <span>{track.artistName}</span>
            )}
            {/* Play count pill — only shown when the track surfaces a count */}
            {Number(track.plays || track.weekly_plays || track.weeklyPlays || 0) > 0 && (
              <span
                data-testid={`track-plays-${track.trackId}`}
                className="inline-flex items-center gap-1 text-[10px] text-white/45 tabular-nums"
              >
                <Headphones size={10} />
                {Number(track.plays || track.weekly_plays || track.weeklyPlays || 0).toLocaleString()}
              </span>
            )}
          </div>
        </div>
      </div>

      {showAlbum && (
        <div className="hidden sm:block text-sm text-white/50 truncate">
          {track.collectionId ? (
            <Link to={`/album/${track.collectionId}`} className="hover:text-white hover:underline">
              {track.collectionName}
            </Link>
          ) : (
            track.collectionName
          )}
        </div>
      )}

      <button
        onClick={handleLike}
        data-testid={`track-like-${track.trackId}`}
        className="opacity-0 group-hover:opacity-100 p-2 text-white/60 hover:text-white"
      >
        <Flame size={16} fill={liked ? "#FF2E00" : "none"} stroke={liked ? "#FF2E00" : "currentColor"} />
      </button>

      <div className="flex items-center gap-2">
        <span className="text-xs text-white/40 font-mono tabular-nums hidden sm:inline">
          {formatTime(track.trackTimeMillis)}
        </span>
        <DropdownMenu onOpenChange={(o) => o && loadMyPlaylists()}>
          <DropdownMenuTrigger asChild>
            <button
              data-testid={`track-more-${track.trackId}`}
              className="opacity-0 group-hover:opacity-100 p-1.5 text-white/60 hover:text-white"
            >
              <MoreHorizontal size={16} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-[#111] border-white/10 text-white">
            <DropdownMenuLabel>Track options</DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-white/10" />
            <DropdownMenuItem onClick={handleLike} className="cursor-pointer">
              <Flame size={14} className="mr-2" />
              {liked ? "Remove from Liked" : "Save to Liked"}
            </DropdownMenuItem>
            {user && playlists.length > 0 && (
              <>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuLabel className="text-xs text-white/40">Add to playlist</DropdownMenuLabel>
                {playlists.map((p) => (
                  <DropdownMenuItem
                    key={p.playlist_id}
                    onClick={() => addTo(p.playlist_id)}
                    className="cursor-pointer"
                  >
                    <Plus size={14} className="mr-2" />
                    {p.name}
                  </DropdownMenuItem>
                ))}
              </>
            )}
            {!user && (
              <DropdownMenuItem onClick={login} className="cursor-pointer">
                Sign in to add to playlists
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default TrackRow;
